import { useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Download, FileSpreadsheet, Trash2, Plus, Package } from "lucide-react";
import { toast } from "sonner";

const PROFILE_FIELDS = [
  ["customer_name", "Customer Name"],
  ["customer_designation", "Designation"],
  ["customer_company", "Company"],
  ["customer_phone", "Phone"],
  ["customer_email", "Email"],
];
const ROUTE_FIELDS = [
  ["origin", "Origin Address"],
  ["origin_floor", "Origin Floor"],
  ["destination", "Destination Address"],
  ["dest_floor", "Destination Floor"],
];
const DATE_FIELDS = [
  ["packing_date", "Packing Date"],
  ["moving_date", "Moving Date"],
  ["delivery_date", "Delivery Date"],
];
const CATEGORIES = ["Furniture", "Electronics", "Kitchen", "Cloths", "Misc", "Vehicles"];
const catColor = (c) =>
  c === "Furniture" ? "bg-amber-100 text-amber-800 border-amber-300"
    : c === "Electronics" ? "bg-sky-100 text-sky-800 border-sky-300"
      : c === "Vehicles" ? "bg-violet-100 text-violet-800 border-violet-300"
        : c === "Kitchen" ? "bg-rose-100 text-rose-800 border-rose-300"
        : c === "Cloths" ? "bg-pink-100 text-pink-800 border-pink-300"
          : "bg-muted text-muted-foreground border-border";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function ImportLeadsDialog({ onImported }) {
  const [open, setOpen] = useState(false);
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(false);
  const [source, setSource] = useState("");
  const fileRef = useRef(null);

  const reset = () => { setLeads([]); setSource(""); if (fileRef.current) fileRef.current.value = ""; };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/leads/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const parsed = (data.leads || []).map((l) => ({ ...l, items: l.items || [] }));
      setLeads(parsed);
      setSource(data.source || "");
      const totalItems = parsed.reduce((s, l) => s + (l.items?.length || 0), 0);
      if (!parsed.length) toast.error("No leads found in this file. Check the columns or use the sample template.");
      else toast.success(`Parsed ${parsed.length} lead(s) · ${totalItems} article(s) from your ${data.source} file`);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  const setField = (li, key, val) =>
    setLeads((ls) => ls.map((l, i) => (i === li ? { ...l, [key]: val } : l)));
  const setItem = (li, ii, key, val) =>
    setLeads((ls) => ls.map((l, i) => (i === li
      ? { ...l, items: l.items.map((it, j) => (j === ii ? { ...it, [key]: val } : it)) } : l)));
  const addItem = (li) =>
    setLeads((ls) => ls.map((l, i) => (i === li
      ? { ...l, items: [...l.items, { name: "", category: "Misc", quantity: 1, value: 0 }] } : l)));
  const removeItem = (li, ii) =>
    setLeads((ls) => ls.map((l, i) => (i === li ? { ...l, items: l.items.filter((_, j) => j !== ii) } : l)));
  const removeLead = (li) => setLeads((ls) => ls.filter((_, i) => i !== li));

  const declaredTotal = (l) => (l.items || []).reduce((s, it) => s + Number(it.value || 0), 0);

  const importAll = async () => {
    const valid = leads.filter((l) => (l.customer_name || "").trim() || (l.customer_phone || "").trim());
    if (!valid.length) return toast.error("Nothing to import — add a name or phone");
    const payload = valid.map((l) => ({
      ...l,
      article_value: parseFloat(l.article_value) || 0,
      items: (l.items || [])
        .filter((it) => (it.name || "").trim())
        .map((it) => ({
          name: it.name, category: it.category || "Misc",
          quantity: parseInt(it.quantity, 10) || 1, value: parseFloat(it.value) || 0,
        })),
    }));
    setBusy(true);
    try {
      const { data } = await api.post("/leads/import/save", { leads: payload });
      toast.success(`Imported ${data.created} lead(s) with draft quotation(s) into the pipeline`);
      setOpen(false); reset(); onImported?.();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/leads/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "arrow-leads-template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <>
      <Button variant="outline" className="rounded-none gap-2" onClick={() => setOpen(true)} data-testid="import-leads-btn">
        <Upload className="h-4 w-4" /> Import Leads
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-5xl rounded-none max-h-[92vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Deep Lead Importer</DialogTitle></DialogHeader>

          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept=".xlsx,.docx" onChange={onFile} className="hidden" data-testid="import-file-input" />
            <Button className="rounded-none gap-2 font-bold uppercase tracking-wide" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="choose-file-btn">
              <FileSpreadsheet className="h-4 w-4" /> {busy ? "Parsing…" : "Choose Excel / Word file"}
            </Button>
            <Button variant="ghost" className="rounded-none gap-2 text-xs" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download className="h-4 w-4" /> Download sample template
            </Button>
            {source && <span className="text-xs text-muted-foreground">Source: {source} · {leads.length} lead(s)</span>}
          </div>

          <div className="overflow-y-auto flex-1 space-y-6 pr-1 mt-2" data-testid="import-preview">
            {leads.map((l, li) => (
              <div key={li} className="border border-border p-4 space-y-4" data-testid={`import-lead-${li}`}>
                <div className="flex items-center justify-between">
                  <span className="font-heading text-sm uppercase tracking-wide text-muted-foreground">Lead #{li + 1}{l.duplicate_phone && <span className="ml-2 inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" data-testid={`import-dupe-${li}`} title="This phone already exists in the pipeline">Duplicate phone</span>}</span>
                  <Button size="sm" variant="ghost" className="rounded-none h-7 text-destructive gap-1 text-xs" onClick={() => removeLead(li)} data-testid={`remove-lead-${li}`}>
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </Button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {PROFILE_FIELDS.map(([k, lbl]) => (
                    <div key={k} className="space-y-1">
                      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{lbl}</Label>
                      <Input className="rounded-none h-8 bg-card text-xs" value={l[k] || ""} onChange={(e) => setField(li, k, e.target.value)} data-testid={`lead-${li}-${k}`} />
                    </div>
                  ))}
                  {ROUTE_FIELDS.map(([k, lbl]) => (
                    <div key={k} className="space-y-1">
                      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{lbl}</Label>
                      <Input className="rounded-none h-8 bg-card text-xs" value={l[k] || ""} onChange={(e) => setField(li, k, e.target.value)} data-testid={`lead-${li}-${k}`} />
                    </div>
                  ))}
                  {DATE_FIELDS.map(([k, lbl]) => (
                    <div key={k} className="space-y-1">
                      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{lbl}</Label>
                      <Input className="rounded-none h-8 bg-card text-xs" value={l[k] || ""} onChange={(e) => setField(li, k, e.target.value)} data-testid={`lead-${li}-${k}`} />
                    </div>
                  ))}
                  <div className="space-y-1">
                    <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Article Value (₹)</Label>
                    <Input type="number" min="0" placeholder="Optional" className="rounded-none h-8 bg-card text-xs" value={l.article_value ?? ""} onChange={(e) => setField(li, "article_value", e.target.value)} data-testid={`lead-${li}-article_value`} />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide"><Package className="h-3.5 w-3.5" /> Inventory ({l.items?.length || 0})</span>
                    <span className="text-xs text-muted-foreground">Declared value: <b className="text-foreground">{inr(declaredTotal(l))}</b></span>
                  </div>
                  <div className="border border-border max-h-64 overflow-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead className="sticky top-0 bg-muted text-[11px] uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className="px-2 py-1.5 text-left">Article</th>
                          <th className="px-2 py-1.5 text-left w-40">Category</th>
                          <th className="px-2 py-1.5 text-left w-20">Qty</th>
                          <th className="px-2 py-1.5 text-left w-32">Value (₹)</th>
                          <th className="px-2 w-10" />
                        </tr>
                      </thead>
                      <tbody>
                        {(l.items || []).map((it, ii) => (
                          <tr key={ii} className="border-t border-border" data-testid={`lead-${li}-item-${ii}`}>
                            <td className="px-1 py-1"><Input className="rounded-none h-8 bg-card text-xs" value={it.name || ""} onChange={(e) => setItem(li, ii, "name", e.target.value)} data-testid={`lead-${li}-item-${ii}-name`} /></td>
                            <td className="px-1 py-1">
                              <Select value={it.category || "Misc"} onValueChange={(v) => setItem(li, ii, "category", v)}>
                                <SelectTrigger className="rounded-none h-8 text-xs bg-card" data-testid={`lead-${li}-item-${ii}-category`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-1 py-1"><Input type="number" min="1" className="rounded-none h-8 bg-card text-xs" value={it.quantity ?? 1} onChange={(e) => setItem(li, ii, "quantity", e.target.value)} data-testid={`lead-${li}-item-${ii}-qty`} /></td>
                            <td className="px-1 py-1"><Input type="number" min="0" className="rounded-none h-8 bg-card text-xs" value={it.value ?? 0} onChange={(e) => setItem(li, ii, "value", e.target.value)} data-testid={`lead-${li}-item-${ii}-value`} /></td>
                            <td className="px-1"><Button size="icon" variant="ghost" className="rounded-none h-8 w-8 text-destructive" onClick={() => removeItem(li, ii)} data-testid={`lead-${li}-item-${ii}-del`}><Trash2 className="h-4 w-4" /></Button></td>
                          </tr>
                        ))}
                        {!(l.items || []).length && (
                          <tr><td colSpan={5} className="px-2 py-3 text-center text-xs text-muted-foreground">No articles detected — add rows manually or check the file.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <Button size="sm" variant="outline" className="rounded-none h-7 gap-1 text-xs" onClick={() => addItem(li)} data-testid={`lead-${li}-add-item`}>
                      <Plus className="h-3.5 w-3.5" /> Add Article
                    </Button>
                    <Badge variant="outline" className="rounded-none text-[10px]">Draft quotation will total {inr(declaredTotal(l))}</Badge>
                  </div>
                </div>
              </div>
            ))}
            {leads.length > 0 && (
              <div className="flex justify-end border-t border-border pt-2 text-sm" data-testid="import-total-article-value">
                Total Article Value: <b className="ml-2 font-mono-num">{inr(leads.reduce((s, l) => s + (parseFloat(l.article_value) || 0), 0))}</b>
              </div>
            )}
            {!leads.length && !busy && (
              <div className="text-center text-sm text-muted-foreground py-10">Upload an Excel or Word file to preview the customer, route and full inventory before importing.</div>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button variant="ghost" className="rounded-none" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
            <Button className="rounded-none gap-2 font-bold uppercase tracking-wide" onClick={importAll} disabled={busy || !leads.length} data-testid="import-all-btn">
              <Upload className="h-4 w-4" /> Import {leads.length} Lead{leads.length !== 1 ? "s" : ""}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
